import React, { useEffect, useState } from "react";
import { Button } from "@fluentui/react-components";
import { useNavigate, useParams } from "react-router-dom";
import { showToast } from "#root/components/GlobalToaster";
import { ROUTES } from "#root/routes/constants";
import {
  useSuperAdminById,
  useUpdateSuperAdmin,
  useResetPasswordSuperAdmin,
} from "#root/queries/superAdmin";
import SuperAdminForm from "../SuperAdminForm";
import { LockClosed24Filled } from "@fluentui/react-icons";
import { DialogBox } from "#root/components/DialogBox";

interface SuperAdminFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

const initialFormData: SuperAdminFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
};

const EditSuperAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isError } = useSuperAdminById({ id: id ?? "" });
  const updateSuperAdminMutation = useUpdateSuperAdmin();
  const resetPasswordMutation = useResetPasswordSuperAdmin();

  const [formData, setFormData] = useState<SuperAdminFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] = useState(false);

  useEffect(() => {
    if (data?.item) {
      const { firstName, lastName, email, phoneNumber } = data.item;
      setFormData({
        firstName,
        lastName,
        email,
        phoneNumber: phoneNumber || "",
      });
    }
  }, [data]);

  const handleInputChange = (field: keyof SuperAdminFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdateDialogOpen(true);
  };

  const handleConfirmUpdate = async () => {
    if (!id) return;
    try {
      await updateSuperAdminMutation.mutateAsync({
        id,
        superAdmin: formData,
      });
      showToast("SuperAdmin details updated successfully", "success");
      navigate(ROUTES.EMC_MERRGE_SETTINGS.SUPERADMIN_USER_MANAGEMENT);
    } catch (error) {
      showToast("Failed to update Super Admin", "error");
    } finally {
      setIsUpdateDialogOpen(false);
    }
  };

  const openResetPasswordDialog = () => {
    setIsResetPasswordDialogOpen(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!id) return;
    try {
      await resetPasswordMutation.mutateAsync({ id });
      showToast("Reset Password email has been sent", "success");
      navigate(ROUTES.EMC_MERRGE_SETTINGS.SUPERADMIN_USER_MANAGEMENT);
    } catch (error) {
      showToast("Failed to reset password", "error");
    } finally {
      setIsResetPasswordDialogOpen(false);
    }
  };

  if (isLoading) {
    return <div className="edit-superadmin__loading">Loading...</div>;
  }

  if (isError || !data?.item) {
    return (
      <div className="edit-superadmin__error">
        Failed to load SuperAdmin data.
      </div>
    );
  }

  return (
    <>
      <SuperAdminForm
        title="Edit SuperAdmin"
        formData={formData}
        errors={errors}
        onChange={handleInputChange}
        onSubmit={handleSubmit}
        actions={
          <>
            <Button type="submit" appearance="primary">
              Update SuperAdmin
            </Button>
            <Button appearance="primary" type="button" onClick={openResetPasswordDialog} icon={<LockClosed24Filled />}>
              Reset Password
            </Button>
            <Button
              appearance="secondary"
              type="button"
              onClick={() => navigate(ROUTES.EMC_MERRGE_SETTINGS.SUPERADMIN_USER_MANAGEMENT)}
            >
              Cancel
            </Button>
          </>
        }
      />

      <DialogBox
        open={isUpdateDialogOpen}
        onOpenChange={setIsUpdateDialogOpen}
        title="Confirm Update"
        primaryButtonText="Confirm"
        onPrimaryButtonClick={handleConfirmUpdate}
        showSecondaryButton={true}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setIsUpdateDialogOpen(false)}
      >
        Are you sure you want to update the SuperAdmin details?
      </DialogBox>

      <DialogBox
        open={isResetPasswordDialogOpen}
        onOpenChange={setIsResetPasswordDialogOpen}
        title="Confirm Reset Password"
        primaryButtonText="Confirm"
        onPrimaryButtonClick={handleConfirmResetPassword}
        showSecondaryButton={true}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setIsResetPasswordDialogOpen(false)}
      >
        Are you sure you want to reset the password for this SuperAdmin?
      </DialogBox>
    </>
  );
};

export default EditSuperAdmin;
